import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Typography, List, ListItem, ListItemIcon, ListItemText, IconButton } from '@mui/material';
import { CheckCircle, Cancel, Close } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

import THEME from '../../constants/theme';
import { SERVICE_STATUS, getOverallStatus, getStatusText } from '../../constants/serviceStatus';

const ServiceStatusModal = ({ open, onClose }) => {
  const overallStatus = getOverallStatus();
  const statusText = getStatusText();

  // Add CSS animation styles
  const shimmerKeyframes = `
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
  `;

  // Inject styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = shimmerKeyframes;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [shimmerKeyframes]);

  const services = [
    {
      name: 'Sentinel',
      status: SERVICE_STATUS.sentinel || SERVICE_STATUS.octopusSync,
      description: 'Accounts Monitoring Service'
    }
  ];

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.7,
      y: -80,
      rotateX: 15
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      rotateX: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: -50,
      rotateX: 10,
      transition: {
        duration: 0.25,
        ease: [0.55, 0.085, 0.68, 0.53]
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Box
              sx={{
                position: 'relative',
                width: { xs: '350px', sm: '420px' },
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(25px)',
                borderRadius: THEME.BORDERS.RADIUS.LARGE,
                boxShadow: '0 25px 80px rgba(169, 64, 255, 0.18), 0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                padding: '36px',
                outline: 'none',
                overflow: 'hidden'
              }}
            >
              {/* Close Button */}
              <IconButton
                onClick={onClose}
                sx={{
                  position: 'absolute',
                  top: '18px',
                  right: '18px',
                  color: THEME.COLORS.NEUTRAL_GRAY,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  width: '36px',
                  height: '36px',
                  '&:hover': {
                    color: THEME.COLORS.PRIMARY_PURPLE,
                    backgroundColor: 'rgba(169, 64, 255, 0.12)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <Close />
              </IconButton>

              {/* Header */}
              <Box sx={{ textAlign: 'center', marginBottom: '28px', paddingTop: '8px' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                    fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                    color: THEME.COLORS.NEUTRAL_BLACK,
                    marginBottom: '16px',
                    fontSize: '1.4rem'
                  }}
                >
                  System Status
                </Typography>
                
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '14px',
                    padding: '16px 24px',
                    borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                    backgroundColor: overallStatus 
                      ? 'rgba(76, 175, 80, 0.08)' 
                      : 'rgba(244, 67, 54, 0.08)',
                    border: `2px solid ${overallStatus ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': overallStatus ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(76, 175, 80, 0.1) 50%, transparent 100%)',
                      animation: 'shimmer 2s infinite',
                    } : {}
                  }}
                >
                  {overallStatus ? (
                    <CheckCircle sx={{ color: '#4CAF50', fontSize: '28px' }} />
                  ) : (
                    <Cancel sx={{ color: '#F44336', fontSize: '28px' }} />
                  )}
                  <Typography
                    sx={{
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                      color: overallStatus ? '#2E7D32' : '#C62828',
                      fontSize: '1.2rem',
                      textShadow: overallStatus 
                        ? '0 1px 2px rgba(76, 175, 80, 0.1)' 
                        : '0 1px 2px rgba(244, 67, 54, 0.1)'
                    }}
                  >
                    {statusText}
                  </Typography>
                </Box>
              </Box>

              {/* Services List */}
              <List sx={{ padding: 0 }}>
                {services.map((service, index) => (
                  <motion.div
                    key={service.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                  >
                    <ListItem
                      sx={{
                        borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                        margin: '10px 0',
                        padding: '18px',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        border: '1px solid rgba(169, 64, 255, 0.12)',
                        transition: 'all 0.4s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          backgroundColor: 'rgba(169, 64, 255, 0.08)',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 35px rgba(169, 64, 255, 0.18)',
                          borderColor: 'rgba(169, 64, 255, 0.18)'
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          backgroundColor: service.status ? '#4CAF50' : '#F44336',
                          borderRadius: '0 2px 2px 0'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: '44px', marginLeft: '8px' }}>
                        {service.status ? (
                          <CheckCircle sx={{ 
                            color: '#4CAF50', 
                            fontSize: '22px',
                            filter: 'drop-shadow(0 2px 4px rgba(76, 175, 80, 0.25))'
                          }} />
                        ) : (
                          <Cancel sx={{ 
                            color: '#F44336', 
                            fontSize: '22px',
                            filter: 'drop-shadow(0 2px 4px rgba(244, 67, 54, 0.25))'
                          }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                              color: THEME.COLORS.NEUTRAL_BLACK,
                              fontSize: '1rem'
                            }}
                          >
                            {service.name}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            sx={{
                              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.LIGHT,
                              color: THEME.COLORS.NEUTRAL_GRAY,
                              fontSize: '0.85rem',
                              marginTop: '4px'
                            }}
                          >
                            {service.description}
                          </Typography>
                        }
                      />
                      <Box
                        sx={{
                          marginLeft: '18px',
                          padding: '6px 14px',
                          borderRadius: THEME.BORDERS.RADIUS.SMALL,
                          backgroundColor: service.status 
                            ? 'rgba(76, 175, 80, 0.12)' 
                            : 'rgba(244, 67, 54, 0.12)',
                          border: `1.5px solid ${service.status ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                          minWidth: '70px',
                          textAlign: 'center'
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                            color: service.status ? '#4CAF50' : '#F44336',
                            fontSize: '0.8rem'
                          }}
                        >
                          {service.status ? 'Online' : 'Offline'}
                        </Typography>
                      </Box>
                    </ListItem>
                  </motion.div>
                ))}
              </List>

              {/* Footer */}
              <Box
                sx={{
                  marginTop: '28px',
                  padding: '18px',
                  borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                  backgroundColor: 'rgba(101, 17, 175, 0.04)',
                  border: '1px solid rgba(101, 17, 175, 0.08)',
                  textAlign: 'center',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60px',
                    height: '2px',
                    backgroundColor: 'rgba(101, 17, 175, 0.2)',
                    borderRadius: '1px'
                  }
                }}
              >
                <Typography
                  sx={{
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                    fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.LIGHT,
                    color: THEME.COLORS.NEUTRAL_GRAY,
                    fontSize: '0.8rem'
                  }}
                >
                  Last updated: {new Date().toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
};

ServiceStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ServiceStatusModal;
